import { Application, Applications, DataDeletionForm, DataDeletionFormValidator } from "@copetence/shared";
import { zodResolver } from "@hookform/resolvers/zod";
import NotificationBanner from "components/banner/Banner.comp";
import { TextController } from "components/textController/TextController.comp";
import { API } from "handlers/APIHandler";
import { useAppContext } from "providers/ContextProvider";
import React, { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import s from "../tickets/create/createTicket.module.scss";

const DataDeletion: React.FC = () => {
    const { appId, email } = useAppContext();

    const [success, setSuccess] = useState<boolean | null>(null);
    const [errorState, setErrorState] = useState<string | null>(null);

    const enabledApps: Application[] = useMemo(() => {
        return Applications.filter(a => a.dataDeletion?.enabled);
    }, []);

    const defaultAppId = useMemo(() => {
        if (appId) return appId;
        return enabledApps[0]?.id || "";
    }, [appId, enabledApps]);

    const { control, handleSubmit, setValue, reset, clearErrors } = useForm<DataDeletionForm>({
        defaultValues: {
            applicationId: defaultAppId,
            enquirerEmail: email || "",
            reason: "",
        },
        resolver: zodResolver(DataDeletionFormValidator),
        mode: "onSubmit",
        reValidateMode: "onChange",
    });

    useEffect(() => {
        if (email) setValue("enquirerEmail", email);
    }, [email, setValue]);

    const onSubmit = (values: DataDeletionForm) => {
        API.post<{}>("/tickets/dataDeletion", {
            body: values
        }).then(() => {
            reset();
            clearErrors();
            setSuccess(true);
        }).catch((err) => {
            console.error(err);
            setErrorState("Submission failed");
            setSuccess(false);
        });
    };

    useEffect(() => {
        const timer = setTimeout(() => setSuccess(null), 5000);
        return () => clearTimeout(timer);
    }, [success]);

    return (
        <>
            <div className={s.reportProblem}>
                <div className={s.container}>
                    <div className={s.form}>
                        <p className={s.subtitle} style={{ marginTop: 0, marginBottom: 20 }}>Request Data Deletion</p>
                        <p className={s.blockDescription}>
                            Please provide the necessary details to process your request.
                        </p>

                        <div className={s.inputGroup}>
                            {/* Application */}
                            {appId ? (
                                <TextController
                                    name="applicationId"
                                    control={control}
                                    label="Application"
                                    type="text"
                                    displayValue={enabledApps.find(a => a.id === appId)?.name || appId}
                                    disabled
                                />
                            ) : (
                                <div className={s.inputContainer}>
                                    <label className={s.label}>Application</label>
                                    <Controller
                                        control={control}
                                        name="applicationId"
                                        render={({ field, fieldState }) => (
                                            <>
                                                <select className={s.input} {...field}>
                                                    {enabledApps.map(app => (
                                                        <option key={app.id} value={app.id}>{app.name}</option>
                                                    ))}
                                                </select>
                                                {fieldState.error && (
                                                    <div className={s.errorText}><p>{fieldState.error.message}</p></div>
                                                )}
                                            </>
                                        )}
                                    />
                                </div>
                            )}

                            {/* Reason */}
                            <TextController
                                name="reason"
                                control={control}
                                label="Reason"
                                type="text"
                                asTextarea
                                placeholder="Please describe your request..."
                            />
                        </div>

                        <p className={s.subtitle}>Contact Details</p>
                        <p className={s.blockDescription}>
                            We might need to contact you for further information.
                        </p>
                        <div className={s.inputGroup}>
                            <TextController
                                name="enquirerEmail"
                                control={control}
                                label="Email"
                                type="email"
                                placeholder="Your email address"
                            />
                            <TextController
                                name="enquirerName"
                                control={control}
                                label="Name (optional)"
                                type="text"
                                placeholder="Your name"
                            />
                            <TextController
                                name="enquirerPhone"
                                control={control}
                                label="Phone (optional)"
                                type="tel"
                                placeholder="Your phone number"
                            />
                        </div>

                        {success != null ? success ?
                            <NotificationBanner message="Request submitted successfully!" type="success" /> :
                            <NotificationBanner message="Error submitting request!" type="error" />
                            : null
                        }

                        <button className={s.submitBTN} onClick={() => {
                            handleSubmit(
                                values => {
                                    onSubmit(values);
                                },
                                errors => {
                                    console.log("errors", errors);
                                })();
                        }}>Submit Request</button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default DataDeletion;


